import { useTokenManager } from '@/composables/useTokenManager'
import { captureError } from '@/observability/sentry'
import {
  useDeployProgressStore,
  type DeployPayload,
  type DeployProgressEntry,
} from '@/store/deployProgress'
import log from '@/utils/useLogger'
import { storeToRefs } from 'pinia'
import { computed, type ComputedRef } from 'vue'

/**
 * FLY3D-612 — WebSocket client for live dock-deploy progress.
 *
 * Connects to IFB-64 (`wss://…/prod?token=<cognito id_token>`),
 * subscribes by `dock_id`, parses every push into the
 * `deployProgress` Pinia store, and tears itself down cleanly on a
 * terminal payload (`poll_again: false`) or explicit stop.
 *
 * **Lifecycle is module-scoped, not component-scoped.** A WebSocket
 * is bound to the deploy it's watching — not to any Vue component
 * that happens to be visible at the time. The flagship case for this
 * is the device-list deployDockModal: the operator triggers a deploy
 * and closes the modal. The deploy is not done. Without module-scope
 * ownership the modal's `onBeforeUnmount` would kill the socket and
 * the device-list pair button would silently lose all progress
 * updates. So:
 *
 *   - Open sockets are tracked in `OPEN_SOCKETS`, keyed by `dock_id`.
 *   - `start(dockId)` is idempotent — if a socket is already open or
 *     connecting for that dock, it returns early.
 *   - Sockets close automatically on `poll_again: false` and on
 *     explicit `stop(dockId)`. They never auto-close on unmount.
 *   - Reconnect uses capped exponential backoff and re-hydrates from
 *     the n8n state snapshot before re-subscribing.
 *
 * Multiple components can call `useDeployProgressSocket()` and share
 * state via the Pinia store; the composable is a thin reactive
 * facade.
 */

const DEFAULT_WS_URL = 'wss://nt9g0qmjhl.execute-api.eu-west-2.amazonaws.com/prod'
const DEFAULT_SNAPSHOT_URL = 'https://idi.app.n8n.cloud/webhook/deploy-state'

const MAX_RECONNECT_ATTEMPTS = 6
const RECONNECT_BASE_MS = 1000

export type SocketStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed' | 'error'

interface SocketSlot {
  dockId: string
  ws: WebSocket | null
  status: SocketStatus
  manuallyClosed: boolean
  reconnectAttempts: number
  reconnectTimer: ReturnType<typeof setTimeout> | null
  /**
   * Caller-supplied correlation id (UUID generated before the GHA
   * dispatch). Sent in the subscribe payload — IFB-64 then filters
   * broadcasts server-side and we only receive events whose
   * `correlation_id` matches. Also validated client-side as defence
   * in depth. Null = unscoped subscribe (general dock monitoring).
   */
  correlationId: string | null
  /**
   * Resolver for the subscribe-ack promise the initial `start()`
   * awaits. IFB-65 only delivers broadcasts to subscribers that
   * match a correlation_id, so the caller MUST be registered before
   * dispatching the GHA — otherwise early events arrive with no
   * matching subscriber and are dropped. Cleared after first ack.
   */
  ackResolver: (() => void) | null
}

const SUBSCRIBE_ACK_TIMEOUT_MS = 8_000

/**
 * Grace period between the subscribe ack and `start()` returning.
 *
 * IFB-64 acks before the subscription is fully visible to IFB-65's
 * poll loop (the broker writes to DynamoDB asynchronously). If the
 * caller dispatches the GHA the moment the ack lands, IFB-65's first
 * poll cycle for the new run finds no matching subscriber and the
 * early step events are dropped.
 *
 * The reference Playwright pipeline test (deploy.js) uses the same
 * 1s pause for exactly this reason — see its comment "ensure
 * DynamoDB has persisted the subscription".
 */
const SUBSCRIBE_SETTLE_MS = 1_500

const OPEN_SOCKETS = new Map<string, SocketSlot>()

let beforeUnloadAttached = false

/** Detach all sockets cleanly when the tab/app is closing. */
const attachBeforeUnloadOnce = () => {
  if (beforeUnloadAttached || typeof window === 'undefined') return
  beforeUnloadAttached = true
  window.addEventListener('beforeunload', () => {
    for (const slot of OPEN_SOCKETS.values()) {
      try {
        if (slot.ws && slot.ws.readyState === WebSocket.OPEN) {
          slot.ws.send(JSON.stringify({ action: 'unsubscribe', dock_id: slot.dockId }))
        }
        slot.ws?.close()
      } catch {
        /* swallow — page is unloading anyway */
      }
    }
    OPEN_SOCKETS.clear()
  })
}

const getWsUrl = (): string =>
  (import.meta.env.VITE_DEPLOY_PROGRESS_WS as string | undefined) ?? DEFAULT_WS_URL

const getSnapshotUrl = (): string =>
  (import.meta.env.VITE_DEPLOY_STATE_SNAPSHOT as string | undefined) ?? DEFAULT_SNAPSHOT_URL

const fetchSnapshot = async (dockId: string) => {
  const store = useDeployProgressStore()
  try {
    const url = `${getSnapshotUrl()}?dock_id=${encodeURIComponent(dockId)}`
    const res = await fetch(url, { method: 'GET' })
    if (!res.ok) {
      log.warn(`useDeployProgressSocket: snapshot ${res.status} for ${dockId}`)
      return
    }
    const payload = (await res.json()) as DeployPayload | null
    if (payload && payload.dock_id) {
      store.applyPayload(payload)
    }
  } catch (err) {
    log.warn('useDeployProgressSocket: snapshot fetch failed', err)
  }
}

const closeSlotSocket = (slot: SocketSlot) => {
  if (!slot.ws) return
  try {
    slot.ws.onopen = null
    slot.ws.onmessage = null
    slot.ws.onerror = null
    slot.ws.onclose = null
    if (slot.ws.readyState === WebSocket.OPEN || slot.ws.readyState === WebSocket.CONNECTING) {
      slot.ws.close()
    }
  } catch (err) {
    log.warn('useDeployProgressSocket: close failed', err)
  }
  slot.ws = null
}

const sendUnsubscribe = (slot: SocketSlot) => {
  if (!slot.ws || slot.ws.readyState !== WebSocket.OPEN) return
  try {
    slot.ws.send(JSON.stringify({ action: 'unsubscribe', dock_id: slot.dockId }))
  } catch (err) {
    log.warn('useDeployProgressSocket: unsubscribe send failed', err)
  }
}

const scheduleReconnect = (slot: SocketSlot) => {
  if (slot.manuallyClosed) return
  if (slot.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
    slot.status = 'error'
    const store = useDeployProgressStore()
    store.setStatus(
      slot.dockId,
      'error',
      'Lost connection to deploy progress feed and could not reconnect.',
    )
    OPEN_SOCKETS.delete(slot.dockId)
    return
  }
  slot.reconnectAttempts += 1
  const delay = Math.min(RECONNECT_BASE_MS * 2 ** (slot.reconnectAttempts - 1), 15_000)
  slot.status = 'reconnecting'
  log.info(
    `useDeployProgressSocket: reconnecting ${slot.dockId} in ${delay}ms (attempt ${slot.reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`,
  )
  slot.reconnectTimer = setTimeout(() => {
    void connect(slot, /*isReconnect*/ true)
  }, delay)
}

const connect = async (slot: SocketSlot, isReconnect: boolean) => {
  if (slot.manuallyClosed) return
  const store = useDeployProgressStore()
  slot.status = isReconnect ? 'reconnecting' : 'connecting'
  if (!isReconnect) store.setStatus(slot.dockId, 'connecting')

  let token: string | null
  try {
    const { getValidToken } = useTokenManager()
    token = await getValidToken(false)
  } catch (err) {
    log.error('useDeployProgressSocket: token fetch failed', err)
    token = null
  }
  if (!token) {
    slot.status = 'error'
    store.setStatus(slot.dockId, 'error', 'Could not obtain auth token for deploy feed.')
    OPEN_SOCKETS.delete(slot.dockId)
    return
  }

  if (isReconnect) {
    // Hydrate the panel from the last-known state before the next push.
    await fetchSnapshot(slot.dockId)
  }

  let ws: WebSocket
  try {
    ws = new WebSocket(`${getWsUrl()}?token=${encodeURIComponent(token)}`)
  } catch (err) {
    const e = err instanceof Error ? err : new Error(String(err))
    captureError(e, {
      context: 'useDeployProgressSocket.connect',
      dockId: slot.dockId,
    })
    slot.status = 'error'
    store.setStatus(slot.dockId, 'error', 'Could not open deploy progress connection.')
    OPEN_SOCKETS.delete(slot.dockId)
    return
  }
  slot.ws = ws

  ws.onopen = () => {
    if (slot.manuallyClosed) {
      closeSlotSocket(slot)
      return
    }
    slot.status = 'open'
    slot.reconnectAttempts = 0
    try {
      const subscribeMsg: Record<string, unknown> = {
        action: 'subscribe',
        dock_id: slot.dockId,
      }
      if (slot.correlationId) subscribeMsg.correlation_id = slot.correlationId
      ws.send(JSON.stringify(subscribeMsg))
    } catch (err) {
      log.warn('useDeployProgressSocket: subscribe send failed', err)
    }
    const e = store.getEntry(slot.dockId)
    if (e && (e.status === 'idle' || e.status === 'connecting')) {
      store.setStatus(slot.dockId, 'live')
    }
  }

  ws.onmessage = (ev: MessageEvent) => {
    let raw: unknown = null
    try {
      raw = JSON.parse(ev.data)
      log.info('useDeployProgressSocket: payload', raw)
    } catch (err) {
      log.warn('useDeployProgressSocket: bad payload', err, ev.data)
      return
    }
    if (!raw || typeof raw !== 'object') return

    // IFB-64 sends `{ type: "subscribed", dock_id, correlation_id }` as
    // the subscribe ack. It carries no progress data — surface it as a
    // status flip and bail before hitting the broadcast path.
    const maybeAck = raw as { type?: string; dock_id?: string; correlation_id?: string }
    if (maybeAck.type === 'subscribed') {
      if (maybeAck.dock_id !== slot.dockId) return
      // Unblock the awaiting `start()` so the caller can now safely
      // dispatch the GHA — the broker has us registered.
      if (slot.ackResolver) {
        slot.ackResolver()
        slot.ackResolver = null
      }
      const e = store.getEntry(slot.dockId)
      if (e && (e.status === 'idle' || e.status === 'connecting')) {
        store.setStatus(slot.dockId, 'live')
      }
      return
    }

    const payload = raw as DeployPayload & { correlation_id?: string }
    if (payload.dock_id !== slot.dockId) return

    // IFB-64 already filters by correlation_id server-side, but
    // double-check here so a misconfigured broker can't surface a
    // foreign run into this UI.
    if (
      slot.correlationId &&
      payload.correlation_id &&
      payload.correlation_id !== slot.correlationId
    ) {
      log.info(
        `useDeployProgressSocket: ignoring payload for ${slot.dockId} (correlation_id mismatch — got ${payload.correlation_id}, expected ${slot.correlationId})`,
      )
      return
    }

    store.applyPayload(payload)
    if (payload.poll_again === false) {
      slot.manuallyClosed = true
      if (slot.reconnectTimer) clearTimeout(slot.reconnectTimer)
      sendUnsubscribe(slot)
      closeSlotSocket(slot)
      slot.status = 'closed'
      OPEN_SOCKETS.delete(slot.dockId)
    }
  }

  ws.onerror = (ev) => {
    log.warn(`useDeployProgressSocket: socket error for ${slot.dockId}`, ev)
  }

  ws.onclose = (ev: CloseEvent) => {
    if (slot.manuallyClosed) {
      slot.status = 'closed'
      return
    }
    log.info(
      `useDeployProgressSocket: ${slot.dockId} closed (code=${ev.code}, reason=${ev.reason || 'n/a'})`,
    )
    slot.ws = null
    scheduleReconnect(slot)
  }
}

export interface UseDeployProgressSocketReturn {
  start: (dockId: string, correlationId?: string | null) => Promise<void>
  stop: (dockId: string, reason?: string) => void
  /** Reactive view of the entry for a given dock_id. */
  entryFor: (dockId: string | null | undefined) => ComputedRef<DeployProgressEntry | null>
  /** Connection-level status for a given dock_id. */
  socketStatusFor: (dockId: string | null | undefined) => ComputedRef<SocketStatus>
  /** True when at least one dock has an open or reconnecting socket. */
  hasActiveDeploys: ComputedRef<boolean>
}

export function useDeployProgressSocket(): UseDeployProgressSocketReturn {
  attachBeforeUnloadOnce()
  const store = useDeployProgressStore()
  const { entries } = storeToRefs(store)

  const start = async (dockId: string, correlationId?: string | null) => {
    if (!dockId) {
      log.warn('useDeployProgressSocket: start() called with empty dockId')
      return
    }
    store.ensureEntry(dockId)

    const existing = OPEN_SOCKETS.get(dockId)
    if (existing) {
      const rs = existing.ws?.readyState
      const isAlive =
        existing.status !== 'closed' &&
        existing.status !== 'error' &&
        (rs === WebSocket.OPEN || rs === WebSocket.CONNECTING)
      const sameCorrelation = (existing.correlationId ?? null) === (correlationId ?? null)
      if (isAlive && sameCorrelation) {
        // Same dock + same run already being watched. No-op.
        return
      }
      if (isAlive && !sameCorrelation) {
        // New deploy run on top of an existing socket — tear down so
        // we re-subscribe with the new correlation_id (the broker keys
        // its server-side filter on it).
        log.info(
          `useDeployProgressSocket: re-opening ${dockId} for new correlation_id (was ${existing.correlationId ?? 'null'}, now ${correlationId ?? 'null'})`,
        )
      }
      // Stale or correlation-changed entry — drop it and re-open below.
      existing.manuallyClosed = true
      if (existing.reconnectTimer) clearTimeout(existing.reconnectTimer)
      sendUnsubscribe(existing)
      closeSlotSocket(existing)
      OPEN_SOCKETS.delete(dockId)
    }

    const slot: SocketSlot = {
      dockId,
      ws: null,
      status: 'idle',
      manuallyClosed: false,
      reconnectAttempts: 0,
      reconnectTimer: null,
      correlationId: correlationId ?? null,
      ackResolver: null,
    }

    // Build the ack promise BEFORE connect() so the resolver is in
    // place when onmessage fires — otherwise a fast broker could ack
    // before we'd had a chance to register the resolver.
    const ackPromise = new Promise<void>((resolve) => {
      slot.ackResolver = resolve
      setTimeout(() => {
        if (slot.ackResolver) {
          log.warn(
            `useDeployProgressSocket: subscribe ack timeout for ${dockId} after ${SUBSCRIBE_ACK_TIMEOUT_MS}ms — proceeding anyway`,
          )
          slot.ackResolver()
          slot.ackResolver = null
        }
      }, SUBSCRIBE_ACK_TIMEOUT_MS)
    })

    OPEN_SOCKETS.set(dockId, slot)
    await connect(slot, /*isReconnect*/ false)
    await ackPromise
    // Let the broker's subscription record settle into DynamoDB before
    // returning — see SUBSCRIBE_SETTLE_MS comment for the full why.
    await new Promise((r) => setTimeout(r, SUBSCRIBE_SETTLE_MS))
  }

  const stop = (dockId: string, reason: string = 'manual') => {
    const slot = OPEN_SOCKETS.get(dockId)
    if (!slot) return
    slot.manuallyClosed = true
    if (slot.reconnectTimer) {
      clearTimeout(slot.reconnectTimer)
      slot.reconnectTimer = null
    }
    sendUnsubscribe(slot)
    closeSlotSocket(slot)
    slot.status = 'closed'
    OPEN_SOCKETS.delete(dockId)
    log.info(`useDeployProgressSocket: stopped ${dockId} (${reason})`)
  }

  const entryFor = (dockId: string | null | undefined) =>
    computed(() => (dockId ? (entries.value[dockId] ?? null) : null))

  const socketStatusFor = (dockId: string | null | undefined) =>
    computed<SocketStatus>(() => {
      if (!dockId) return 'idle'
      return OPEN_SOCKETS.get(dockId)?.status ?? 'idle'
    })

  const hasActiveDeploys = computed(() => OPEN_SOCKETS.size > 0)

  return { start, stop, entryFor, socketStatusFor, hasActiveDeploys }
}
